export default class EventEmitter {
  constructor () {
    this._listeners = {};
  }

  /**
   * Bind a new event listener
   *
   * @param {string}   type     The type of event to bind to
   * @param {function} callback The handler to call when the event is fired
   * @param {object}   options  Additional options
   *
   * @return {function} A function that removes the event binding
   */
  on (type, callback, options = {
    context: null,
    once: false
  }) {
    if (!this._listeners[type]) {
      this._listeners[type] = [];
    }

    if (typeof callback !== 'function') {
      return;
    }

    const listener = {
      context: options.context,
      callback: callback,
      once: options.once
    };

    this._listeners[type].push(listener);

    return this.off.bind(this, type, callback);
  }

  /**
   * Bind a new event listener that removes itself after firing the first time
   *
   * @param {string}   type     The type of event to bind to
   * @param {function} callback The handler to call when the event is fired
   *
   * @return {function} A function that removes the event binding
   */
  one (type, callback, options = {
    context: null
  }) {
    options.once = true;
    return this.on(type, callback, options);
  }

  /**
   * Unbind an existing event handler
   *
   * @param {string}   type The type of eent to unbind from
   * @param {function} fn   The handler called when the event is fired
   */
  off (type, callback, options = {
    once: false,
    context: null
  }) {
    if (!this._listeners[type]) return;

    this._listeners[type] = this._listeners[type].filter(function (listener) {
      return (listener.callback !== callback);
    });
  }

  /**
   * Triggers event listeners for an event
   *
   * @param {object} event The event to be triggered
   * @return {object}
   */
  emit (event) {
    const listeners = (this._listeners[event.type] || []).concat(this._listeners['all'] || []);

    for (let i = 0, len = listeners.length; i < len; i++) {
      const listener = listeners[i];

      if (listener.once) {
        this.off(event.type, listener.callback);
      }

      listener.callback.call(listener.context, event);
    }

    return event;
  }
}
